import React from 'react';
import { 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    Typography, 
} from '@mui/material';
import { MdWarning, MdDelete, MdInfo, MdHelp } from 'react-icons/md';

/**
 * A generic and beautiful confirmation dialog.
 * @param {boolean} open - Whether the dialog is open.
 * @param {function} onClose - Function to call on cancel/close.
 * @param {function} onConfirm - Function to call on confirmation.
 * @param {string} title - The title of the dialog.
 * @param {string} message - The message body.
 * @param {string} confirmText - Text for the confirm button.
 * @param {string} cancelText - Text for the cancel button.
 * @param {string} type - The type of dialog ('delete', 'warning', 'info', 'question').
 */
const ConfirmModal = ({ 
    open, 
    onClose, 
    onConfirm, 
    title = "Bestätigen", 
    message = "Sind Sie sicher?", 
    confirmText = "Bestätigen", 
    cancelText = "Abbrechen",
    type = "question"
}) => {
    
    const getIcon = () => {
        switch (type) {
            case 'delete': return <MdDelete size={32} color="#d32f2f" />;
            case 'warning': return <MdWarning size={32} color="#ed6c02" />;
            case 'info': return <MdInfo size={32} color="#0288d1" />;
            default: return <MdHelp size={32} color="#2e7d32" />;
        }
    };

    const getConfirmButtonColor = () => {
        switch (type) {
            case 'delete': return 'error';
            case 'warning': return 'warning';
            case 'info': return 'info';
            default: return 'success';
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="xs" 
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, p: 1 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
                {getIcon()}
                <Typography variant="h6" component="span" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
                    {message}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined" 
                    color="inherit"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                >
                    {cancelText}
                </Button>
                <Button 
                    onClick={() => {
                        onConfirm();
                        onClose();
                    }} 
                    variant="contained" 
                    color={getConfirmButtonColor()}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', px: 3 }}
                    autoFocus
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmModal;
